import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useProducts} from '@salesforce/commerce-sdk-react'
import ProductScroller from '../../../../product-scroller'
import {useGlobaleContext} from '../../../../../contexts/globale'
import ShopThisLookModal from './modal'
import {SELECTED_ATTRIBUTES_WIDGET_PRODUCTS} from '../../../../../constants'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import usePdTagManager from '../../../../../hooks/use-pd-tag-manager'

export const ProductsList = ({
    onTileClick = () => {},
    dataLayerLoaded = true,
    openModalWithTiles,
    typeId,
    name,
    ...rest
}) => {
    const pdTagManager = usePdTagManager()
    const {countryISO, currencyCode} = useGlobaleContext()
    const selectedAttributes = SELECTED_ATTRIBUTES_WIDGET_PRODUCTS
    const analyticsProps = {
        component_id: rest.variant || typeId,
        component_name: rest.title || rest.name || 'ShopTheLook'
    }
    const productIds = []
    let index = 1
    while (`product${index}` in rest) {
        productIds.push(rest[`product${index}`])
        delete rest[`product${index}`] // Avoid modifying rest if unnecessary
        index++
    }
    const enabledScapi = Boolean(productIds.length) && !isServer
    const {data: products, isLoading} = useProducts(
        {
            parameters: {
                ids: productIds.join(','),
                expand: ['availability', 'links', 'promotions', 'images', 'prices', 'variations'],
                perPricebook: true,
                select: selectedAttributes,
                allVariationProperties: true,
                allImages: true,
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode
            }
        },
        {
            enabled: enabledScapi,
            keepPreviousData: true
        }
    )

    useEffect(() => {
        if (productIds?.length) {
            pdTagManager.viewComponent(typeId, name, productIds?.length)
        }
    }, [productIds?.length])

    if (productIds.length === 0) {
        return null
    }

    return (
        <Box
            className="shop-the-look-slider"
            marginBottom={{base: '5px', lg: '0'}}
            sx={{
                '& .slick-slider': {
                    paddingTop: '0 !important'
                },
                '& .slick-slide': {
                    height: {base: '96px', md: '100%'}
                },
                '& div[data-testid="product-scroller"]': {
                    padding: '0 !important'
                },
                '& .slick-track': {
                    display: 'inline-flex',
                    padding: {base: '20px 30px', md: 'unset'}
                },
                '& b': {
                    fontSize: {base: '14px', md: '14px', '2md': '16px'}
                },
                '& s': {
                    fontSize: {base: '14px', md: '14px', '2md': '16px'}
                },
                '& div[data-testid="product-tile-image"]': {
                    width: {base: 'fit-content', md: '100%'}
                },
                '& div[data-testid="product-scroller-item"]': {
                    display: {base: 'inline-flex', md: 'unset'},
                    height: {base: '96px', md: 'unset'}
                },
                '& img': {
                    minWidth: {base: '77px', md: '100%'},
                    height: {base: '96px', md: '100%'}
                },
                '& .info-wrapper': {
                    margin: {
                        base: '0 10px 0 20px',
                        md: '13.93px 10px 0 10px',
                        lg: '13.93px 10px 0 10px',
                        xl: '13.93px 10px 0 10px'
                    }
                },
                '& .product-name': {
                    margin: {md: '0px 10px 5px 0', lg: '0px 10px 10px 0', xl: '0px 10px 13.93px 0'},
                    fontSize: {md: '18px', base: '18px'},
                    minHeight: {md: '44px', lg: '54px'}
                },
                '& .tiles-skeleton-wrapper': {
                    margin: {base: '20px 15px 20px 15px', md: '0'}
                },
                '& .skeleton-tile-name': {
                    margin: {
                        md: '13.93px 10px 5px 0',
                        lg: '13.93px 10px 10px 0',
                        xl: '13.93px 10px 13.93px 0'
                    },
                    maxWidth: {md: '100px', lg: '150px'}
                },
                '& div[data-testid="product-scroller-item-skeleton"]': {
                    display: {base: 'inline-flex', md: 'unset'},
                    flexDirection: {base: 'row', md: 'column'}
                },
                '& .skeleton-tile-image': {
                    width: {base: '77px', md: 'unset'},
                    height: {base: '96px', md: 'unset'}
                },
                '& .product-scroller .swiper-wrapper': {
                    paddingLeft: {base: '30px', md: '0'}
                }
            }}
        >
            <ShopThisLookModal
                desktopImage={rest?.desktopImage}
                mobileImage={rest?.mobileImage}
                desktopVideo={rest?.desktopVideo}
                mobileVideo={rest?.mobileVideo}
                desktopVideoThumbnail={rest?.desktopVideoThumbnail}
                mobileVideoThumbnail={rest?.mobileVideoThumbnail}
                videoAlt={rest?.videoAlt}
                videoEnableSound={rest?.videoEnableSound}
                videoDisableAutoPlay={rest?.videoDisableAutoPlay}
                cloudinaryAspectRatio={rest?.cloudinaryAspectRatio}
                products={products}
                modalDisclosure={rest?.modalDisclosure}
                analyticsProps={analyticsProps}
            />

            <ProductScroller
                products={products?.data}
                hideArrows={false}
                itemWidth={{lg: '245px', '2md': '168px', md: '110px', base: '263px'}}
                hideVariations={true}
                slidesToShow={{base: 1, md: 2}}
                showImageSlider={false}
                dataLayerLoaded={dataLayerLoaded}
                isplp={false}
                draggable={true}
                isLoading={isLoading}
                showQuickAdd={false}
                title={rest.title}
                showTitle={false}
                onTileClick={onTileClick}
                preventTileClick={openModalWithTiles}
                analyticsProps={analyticsProps}
            />
        </Box>
    )
}

ProductsList.propTypes = {
    title: PropTypes.string,
    titleColor: PropTypes.string,
    titleTag: PropTypes.string,
    openModalWithTiles: PropTypes.bool,
    dataLayerLoaded: PropTypes.bool,
    typeId: PropTypes.string,
    name: PropTypes.any,
    onTileClick: PropTypes.func,
    analyticsProps: PropTypes.object
}

export default ProductsList
